import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import { api } from "../lib/api";
import { useAuth, isClient, isAppraiser } from "../context/AuthContext";
import { toast } from "sonner";
import { HouseLine, ClipboardText, Trophy, ArrowRight, UploadSimple, Warning, CheckCircle } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const STATUS_STYLES = {
  open: "bg-emerald-100 text-emerald-900",
  awarded: "bg-amber-100 text-amber-900",
  in_progress: "bg-sky-100 text-sky-900",
  submitted: "bg-indigo-100 text-indigo-900",
  in_review: "bg-violet-100 text-violet-900",
  approved: "bg-teal-100 text-teal-900",
  paid: "bg-green-200 text-green-900",
};

export default function JobDetail() {
  const { jobId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [bids, setBids] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [awarding, setAwarding] = useState(null);
  const [complexities, setComplexities] = useState([]);
  const [newBid, setNewBid] = useState({ minimum_fee: "", notes: "", add_ons: [], other_label: "", other_fee: "" });
  const [reportPath, setReportPath] = useState(null);
  const [reportName, setReportName] = useState(null);
  const [submittingWork, setSubmittingWork] = useState(false);
  const [search] = useSearchParams();

  const load = useCallback(async () => {
    const [{ data: j }, { data: b }, { data: r }] = await Promise.all([
      api.get(`/jobs/${jobId}`),
      api.get(`/jobs/${jobId}/bids`),
      api.get(`/reviews/${jobId}`),
    ]);
    setJob(j);
    setBids(b);
    setReviews(r);
  }, [jobId]);

  useEffect(() => {
    load();
    api.get("/complexity-options").then(({ data }) => setComplexities(data)).catch(() => {});
  }, [load]);

  // Handle PayPal return
  useEffect(() => {
    const token = search.get("token"); // paypal order id
    const status = search.get("checkout");
    if (status === "return" && token) {
      (async () => {
        try {
          await api.post(`/jobs/${jobId}/capture`);
          toast.success("Payment captured — funds held in escrow.");
          navigate(`/jobs/${jobId}`, { replace: true });
          load();
        } catch (e) {
          toast.error("Capture failed: " + (e.response?.data?.detail || e.message));
        }
      })();
    }
  }, [search, jobId, navigate, load]);

  if (!job) return <PageLayout><div className="text-zinc-500">Loading…</div></PageLayout>;

  const iOwn = user?.user_id === job.client_user_id;
  const iAmAppraiser = user?.user_id === job.appraiser_user_id;
  const alreadyBid = bids.find(b => b.appraiser_user_id === user?.user_id);
  const canBid = isAppraiser(user) && job.status === "open" && !iOwn && !alreadyBid;

  const totalNewBid = () => {
    const min = Number(newBid.minimum_fee) || 0;
    const addons = newBid.add_ons.reduce((s, a) => s + a.fee, 0);
    const other = Number(newBid.other_fee) || 0;
    return min + addons + other;
  };

  const toggleAddOn = (opt) => {
    const idx = newBid.add_ons.findIndex(a => a.label === opt.label);
    let next;
    if (idx >= 0) next = newBid.add_ons.filter((_, i) => i !== idx);
    else next = [...newBid.add_ons, { label: opt.label, fee: opt.default_fee }];
    setNewBid({ ...newBid, add_ons: next });
  };

  const updateAddOnFee = (label, fee) => {
    setNewBid({ ...newBid, add_ons: newBid.add_ons.map(a => a.label === label ? { ...a, fee: Number(fee) || 0 } : a) });
  };

  const submitBid = async () => {
    if (!newBid.minimum_fee) { toast.error("Enter your minimum fee"); return; }
    if (Number(newBid.minimum_fee) < job.ai_minimum_fee) {
      toast.error(`Minimum must be at least $${job.ai_minimum_fee}`);
      return;
    }
    const add_ons = [...newBid.add_ons];
    if (newBid.other_label && Number(newBid.other_fee)) {
      add_ons.push({ label: `Other: ${newBid.other_label}`, fee: Number(newBid.other_fee) });
    }
    try {
      await api.post(`/jobs/${jobId}/bids`, {
        minimum_fee: Number(newBid.minimum_fee),
        add_ons,
        notes: newBid.notes || null,
      });
      toast.success("Bid submitted");
      setNewBid({ minimum_fee: "", notes: "", add_ons: [], other_label: "", other_fee: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bid failed");
    }
  };

  const awardBid = async (bidId) => {
    setAwarding(bidId);
    try {
      const returnUrl = `${window.location.origin}/jobs/${jobId}?checkout=return`;
      const cancelUrl = `${window.location.origin}/jobs/${jobId}?checkout=cancel`;
      const { data } = await api.post(`/jobs/${jobId}/award`, {
        bid_id: bidId,
        return_url: returnUrl,
        cancel_url: cancelUrl,
      });
      // Redirect to PayPal approval
      window.location.href = data.approval_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Award failed");
      setAwarding(null);
    }
  };

  const uploadReport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReportPath(data.path);
      setReportName(data.filename);
      toast.success("Report uploaded — click Submit for review.");
    } catch (err) { toast.error("Upload failed"); }
  };

  const submitWork = async () => {
    if (!reportPath) { toast.error("Upload the report first"); return; }
    setSubmittingWork(true);
    try {
      await api.post(`/jobs/${jobId}/submit-work`, { report_path: reportPath });
      toast.success("Sent to reviewer pool.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally { setSubmittingWork(false); }
  };

  return (
    <PageLayout>
      <div className="max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="kicker">Job {job.job_id.slice(0, 12)}</div>
            <h1 className="display-serif text-4xl sm:text-5xl mt-2">{job.address}</h1>
            <div className="text-zinc-600 mt-1">{job.city}, {job.state} {job.zip_code}</div>
          </div>
          <span className={`chip ${STATUS_STYLES[job.status] || "bg-zinc-100"} self-start`}>{job.status.replace("_", " ")}</span>
        </div>

        <div className="mt-8 grid lg:grid-cols-[1fr,340px] gap-6">
          {/* Details */}
          <section className="surface-card p-6 lg:p-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              <Fact label="Form" value={job.form_type.replace("_", " ")} />
              <Fact label="House sqft" value={job.house_sqft || "—"} />
              <Fact label="Lot sqft" value={job.lot_size_sqft || "—"} />
              <Fact label="Travel mi" value={job.travel_distance_mi || 0} />
              <Fact label="AI min fee" value={`$${job.ai_minimum_fee?.toFixed(0)}`} highlight />
              {job.escrow_amount && <Fact label="Escrow" value={`$${job.escrow_amount.toFixed(2)}`} />}
              {job.appraiser_payout_amount != null && <Fact label="Appraiser payout" value={`$${job.appraiser_payout_amount.toFixed(2)}`} />}
              {job.platform_fee_amount != null && <Fact label="Platform 6%" value={`$${job.platform_fee_amount.toFixed(2)}`} />}
            </div>
            {job.notes && (
              <div className="mt-6">
                <div className="kicker">Client notes</div>
                <p className="text-zinc-700 mt-1 text-sm whitespace-pre-line">{job.notes}</p>
              </div>
            )}
            {job.ai_rationale && (
              <div className="mt-6 rounded-xl bg-[#FFEDD5]/60 border border-[#FFEDD5] p-4">
                <div className="kicker text-[#9A3412]">AI rationale</div>
                <div className="text-sm text-zinc-800 mt-1">{job.ai_rationale}</div>
              </div>
            )}
          </section>

          {/* Right column: submit work OR quick facts */}
          <aside className="space-y-6">
            {iAmAppraiser && ["in_progress"].includes(job.status) && (
              <div className="surface-card p-6" data-testid="submit-work-panel">
                <div className="kicker">Deliverable</div>
                <h3 className="display-serif text-2xl mt-1">Submit your report</h3>
                <label className="mt-4 flex items-center gap-2 rounded-full border border-dashed border-zinc-300 px-4 py-3 cursor-pointer hover:bg-zinc-50 text-sm">
                  <UploadSimple size={18} />
                  <span>{reportName || "Choose PDF or file"}</span>
                  <input type="file" onChange={uploadReport} className="hidden" data-testid="job-report-upload" />
                </label>
                <button className="btn-ink mt-4 w-full justify-center" disabled={!reportPath || submittingWork} onClick={submitWork} data-testid="submit-work-btn">
                  {submittingWork ? "Submitting…" : "Submit for review"}
                </button>
              </div>
            )}
            {iAmAppraiser && job.status === "awarded" && (
              <div className="surface-card p-6" data-testid="awaiting-payment-panel">
                <Warning size={20} className="text-[#9A3412]" />
                <div className="display-serif text-xl mt-2">Awaiting client payment</div>
                <div className="text-sm text-zinc-600 mt-1">Once the client pays via PayPal, this job moves to "in progress" and you can upload your report.</div>
              </div>
            )}
            {job.report_path && (
              <div className="surface-card p-6">
                <div className="kicker">Report</div>
                <div className="mt-1 text-sm text-zinc-700 break-all">{job.report_path.split("/").pop()}</div>
              </div>
            )}
            {reviews.length > 0 && (
              <div className="surface-card p-6">
                <div className="kicker">Review</div>
                {reviews.map(r => (
                  <div key={r.review_id} className="mt-2">
                    <div className="flex items-center gap-2">
                      {r.decision === "approved" ? <CheckCircle size={16} className="text-emerald-700" weight="fill" /> : <Warning size={16} className="text-amber-700" weight="fill" />}
                      <div className="font-medium capitalize">{r.decision.replace("_", " ")}</div>
                    </div>
                    <div className="text-xs text-zinc-500">By {r.reviewer_name}</div>
                    {r.comment && <div className="text-sm text-zinc-700 mt-1">{r.comment}</div>}
                  </div>
                ))}
              </div>
            )}
          </aside>
        </div>

        {/* Bidding: submit form for appraisers */}
        {canBid && (
          <section className="mt-10 surface-card p-6 lg:p-8" data-testid="bid-submit-section">
            <div className="flex items-center gap-2"><ClipboardText size={18} className="text-[#115E59]" weight="duotone" /><h3 className="display-serif text-2xl">Submit your bid</h3></div>
            <p className="text-zinc-600 text-sm mt-1">Minimum fee is <b>${job.ai_minimum_fee.toFixed(0)}</b>. Add complexity as needed.</p>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Your minimum fee ($)</Label>
                <Input type="number" min={job.ai_minimum_fee} value={newBid.minimum_fee} onChange={e => setNewBid({ ...newBid, minimum_fee: e.target.value })} data-testid="bid-min-fee" />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Input value={newBid.notes} onChange={e => setNewBid({ ...newBid, notes: e.target.value })} data-testid="bid-notes" />
              </div>
            </div>

            <div className="mt-6">
              <div className="kicker">Complexity add-ons</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {complexities.map(opt => {
                  const active = newBid.add_ons.find(a => a.label === opt.label);
                  return (
                    <button
                      key={opt.option_id}
                      onClick={() => toggleAddOn(opt)}
                      data-testid={`bid-addon-${opt.label.toLowerCase().replace(/\s+/g,'-')}`}
                      className={`chip border transition-colors ${
                        active ? "bg-[#9A3412] text-white border-[#9A3412]" : "bg-[#FFEDD5] text-[#9A3412] border-[#FFEDD5]"
                      }`}
                    >
                      {opt.label} · ${active ? active.fee : opt.default_fee}
                    </button>
                  );
                })}
              </div>
              {newBid.add_ons.length > 0 && (
                <div className="mt-3 space-y-2">
                  {newBid.add_ons.map(a => (
                    <div key={a.label} className="flex items-center gap-3">
                      <span className="text-sm flex-1">{a.label}</span>
                      <Input type="number" value={a.fee} onChange={e => updateAddOnFee(a.label, e.target.value)} className="w-28" data-testid={`bid-addon-fee-${a.label.toLowerCase().replace(/\s+/g,'-')}`} />
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 grid sm:grid-cols-[1fr,140px] gap-3">
                <Input placeholder="Other complexity (describe)" value={newBid.other_label} onChange={e => setNewBid({ ...newBid, other_label: e.target.value })} data-testid="bid-other-label" />
                <Input type="number" placeholder="$ fee" value={newBid.other_fee} onChange={e => setNewBid({ ...newBid, other_fee: e.target.value })} data-testid="bid-other-fee" />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <div>
                <div className="kicker">Bid total</div>
                <div className="display-serif text-3xl mt-1">${totalNewBid().toFixed(0)}</div>
              </div>
              <button className="btn-ink" onClick={submitBid} data-testid="bid-submit-btn">Submit bid</button>
            </div>
          </section>
        )}

        {/* Bids list */}
        {(iOwn || isAppraiser(user)) && (
          <section className="mt-10">
            <div className="kicker">Bids ({bids.length})</div>
            <h3 className="display-serif text-2xl mt-1">
              {iOwn ? "Choose the appraiser you want to work with" : "All bids on this job"}
            </h3>

            {/* Mobile: stacked cards; Desktop: table */}
            <div className="mt-6 grid gap-3 lg:hidden" data-testid="bid-list-mobile">
              {bids.map(b => (
                <BidCard key={b.bid_id} bid={b} isWinner={job.winning_bid_id === b.bid_id} iOwn={iOwn && job.status === "open"} onAward={() => awardBid(b.bid_id)} awarding={awarding} />
              ))}
              {bids.length === 0 && <div className="text-zinc-500">No bids yet.</div>}
            </div>
            <div className="mt-6 hidden lg:block surface-card overflow-hidden" data-testid="bid-list-desktop">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50/70 text-zinc-500">
                  <tr>
                    <th className="text-left px-6 py-3 font-medium">Appraiser</th>
                    <th className="text-left px-6 py-3 font-medium">Minimum</th>
                    <th className="text-left px-6 py-3 font-medium">Add-ons</th>
                    <th className="text-right px-6 py-3 font-medium">Total</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {bids.map(b => (
                    <tr key={b.bid_id} className="border-t border-zinc-100">
                      <td className="px-6 py-4">
                        <div className="font-medium">{b.appraiser_name}</div>
                        {b.notes && <div className="text-zinc-500 text-xs mt-0.5">{b.notes}</div>}
                      </td>
                      <td className="px-6 py-4">${b.minimum_fee.toFixed(0)}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {b.add_ons.map((a, i) => (
                            <span key={i} className="chip bg-[#FFEDD5] text-[#9A3412]">{a.label} · ${a.fee}</span>
                          ))}
                          {b.add_ons.length === 0 && <span className="text-zinc-400">—</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right display-serif text-xl">${b.total_fee.toFixed(0)}</td>
                      <td className="px-6 py-4 text-right">
                        {iOwn && job.status === "open" && (
                          <button className="btn-ink" onClick={() => awardBid(b.bid_id)} disabled={awarding === b.bid_id} data-testid={`award-btn-${b.bid_id}`}>
                            <Trophy size={14} weight="fill" /> {awarding === b.bid_id ? "…" : "Award & pay"}
                          </button>
                        )}
                        {job.winning_bid_id === b.bid_id && (
                          <span className="chip bg-emerald-100 text-emerald-900">Winner</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {bids.length === 0 && (
                    <tr><td colSpan="5" className="px-6 py-8 text-center text-zinc-500">No bids yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </PageLayout>
  );
}

function Fact({ label, value, highlight }) {
  return (
    <div>
      <div className="kicker">{label}</div>
      <div className={`mt-1 ${highlight ? "display-serif text-3xl" : "text-lg font-medium"}`}>{value}</div>
    </div>
  );
}

function BidCard({ bid, isWinner, iOwn, onAward, awarding }) {
  return (
    <div className={`rounded-2xl border p-5 ${isWinner ? "border-emerald-400 bg-emerald-50/40" : "bg-white border-zinc-200"}`} data-testid={`bid-card-${bid.bid_id}`}>
      <div className="flex items-center justify-between">
        <div className="font-medium">{bid.appraiser_name}</div>
        <div className="display-serif text-2xl">${bid.total_fee.toFixed(0)}</div>
      </div>
      <div className="mt-2 text-sm text-zinc-600">Minimum ${bid.minimum_fee.toFixed(0)}</div>
      {bid.add_ons.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {bid.add_ons.map((a, i) => <span key={i} className="chip bg-[#FFEDD5] text-[#9A3412]">{a.label} · ${a.fee}</span>)}
        </div>
      )}
      {bid.notes && <div className="mt-3 text-sm text-zinc-600">{bid.notes}</div>}
      {isWinner && <div className="mt-3 chip bg-emerald-100 text-emerald-900 w-fit">Winner</div>}
      {iOwn && !isWinner && (
        <button className="btn-ink mt-4 w-full justify-center" onClick={onAward} disabled={awarding === bid.bid_id} data-testid={`award-btn-mobile-${bid.bid_id}`}>
          {awarding === bid.bid_id ? "Redirecting…" : (<><Trophy size={14} weight="fill" /> Award &amp; pay</>)}
        </button>
      )}
    </div>
  );
}
